// ISDP CUSTOM: Premium Dräger-branded sign-in screen.
//
// Deployed DevHub signs in via the shared Okta app a0057 (oidc provider) — one Dräger login across both
// hubs. Okta is the only provider; the Entra rollback path was removed 2026-10-28.
// Localhost keeps the silent guest auto-sign-in (frictionless dev; Playwright e2e runs in guest mode).
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ProxiedSignInPage, UserIdentity } from '@backstage/core-components';
import {
  useApi,
  errorApiRef,
  type SignInPageProps,
  type OAuthApi,
  type ProfileInfoApi,
  type BackstageIdentityApi,
  type SessionApi,
} from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { oidcAuthApiRef } from '../../apis';
import { authMessages } from '../../i18n/auth';
import './SignInPage.css';

const isLocalhost = () =>
  window.location.hostname === 'localhost' ||
  window.location.hostname === '127.0.0.1';

// E2E hook (localhost only): opt out of the guest auto-sign-in and drive the real Okta oidc flow
// locally. Set by the .NET/Playwright suite via ?okta=1 or localStorage 'devhub-e2e-okta'='1'.
export const forceOktaLocally = () => {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location.search).get('okta') === '1' ||
      window.localStorage.getItem('devhub-e2e-okta') === '1'
    );
  } catch {
    return false;
  }
};

/**
 * True when this screen will silently sign the visitor in as `guest` instead of asking
 * for anything — localhost dev and the Playwright e2e suite. There is then no such thing
 * as an anonymous visitor, which is why App.tsx's root entry must not route one anywhere.
 */
export const usesGuestAutoSignIn = () => isLocalhost() && !forceOktaLocally();

// If the silent session check hasn't resolved by this point, stop showing the
// "signing you in…" state and fall back to the login button.
const AUTH_CHECK_FALLBACK_MS = 4000;

type AuthApiT = OAuthApi & ProfileInfoApi & BackstageIdentityApi & SessionApi;

/** Branded OAuth sign-in screen, parameterised by the provider it drives. */
const OAuthSignInScreen = ({
  onSignInSuccess,
  authApi,
  providerLabel,
  logo,
  hintKey,
  onNoSession,
}: SignInPageProps & {
  onNoSession?: () => void;
  authApi: AuthApiT;
  providerLabel: string;
  logo?: ReactNode;
  hintKey: 'signIn.hintDraeger';
}) => {
  const errorApi = useApi(errorApiRef);
  const { t } = useTranslationRef(authMessages);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    document.body.classList.add('odx-light-mode');
    document.body.classList.remove('odx-dark-mode', 'odx-theme-dark');
  }, []);

  const completeSignIn = useCallback(
    async (identityResponse: { identity: any }) => {
      const profile = await authApi.getProfile();
      onSignInSuccess(
        UserIdentity.create({
          identity: identityResponse.identity,
          profile,
          authApi,
        }),
      );
    },
    [authApi, onSignInSuccess],
  );

  // Read through a ref so a host passing a fresh callback each render does not re-run the
  // silent restore below.
  const onNoSessionRef = useRef(onNoSession);
  onNoSessionRef.current = onNoSession;

  // Silent session restore on mount — don't flash the login screen on remounts.
  useEffect(() => {
    let active = true;
    const fallback = setTimeout(() => active && setChecking(false), AUTH_CHECK_FALLBACK_MS);
    authApi
      .getBackstageIdentity({ optional: true })
      .then(res => {
        if (!active) return;
        if (res) completeSignIn(res);
        else noSession();
      })
      .catch(() => active && noSession())
      .finally(() => clearTimeout(fallback));
    // There is no session to restore. A host that knows somewhere better for this visitor
    // (App.tsx: the anonymous portal, on a public path) takes over — keep the splash up so
    // the sign-in panel never flashes; otherwise show the panel.
    function noSession() {
      const handOff = onNoSessionRef.current;
      if (handOff) {
        clearTimeout(fallback);
        handOff();
      } else {
        setChecking(false);
      }
    }
    return () => {
      active = false;
      clearTimeout(fallback);
    };
  }, [authApi, completeSignIn]);

  const handleLogin = useCallback(async () => {
    setBusy(true);
    try {
      const identityResponse = await authApi.getBackstageIdentity({ instantPopup: true });
      if (!identityResponse) {
        throw new Error(`The ${providerLabel} provider is not configured to support sign-in`);
      }
      await completeSignIn(identityResponse);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      errorApi.post(new Error(`Login failed; ${message}`));
      setBusy(false);
    }
  }, [authApi, errorApi, completeSignIn, providerLabel]);

  let buttonLabel: string = t('signIn.button', { provider: providerLabel });
  if (busy) buttonLabel = t('signIn.signingIn');

  // While the silent session check is in flight (e.g. right after an F5), show a
  // spinner splash instead of flashing the full sign-in panel — the session usually
  // restores and the user is never actually prompted. Falls through to the panel only
  // if the check resolves to "not signed in".
  if (checking) {
    return (
      <div className="devhub-signin">
        <div className="devhub-signin__backdrop" aria-hidden="true" />
        <div className="devhub-signin__panel devhub-signin__panel--checking">
          <div className="devhub-signin__brand">
            <div className="devhub-signin__logo" role="img" aria-label="Dräger" />
            <span className="devhub-signin__wordmark">DevHub</span>
          </div>
          <div className="devhub-signin__spinner" aria-hidden="true" />
          <p className="devhub-signin__subtitle">{t('signIn.signingYouIn')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="devhub-signin">
      <div className="devhub-signin__backdrop" aria-hidden="true" />
      <div className="devhub-signin__panel">
        <div className="devhub-signin__brand">
          <div className="devhub-signin__logo" role="img" aria-label="Dräger" />
          <span className="devhub-signin__wordmark">DevHub</span>
        </div>

        <h1 className="devhub-signin__title">{t('signIn.welcomeBack')}</h1>
        <p className="devhub-signin__subtitle">{t('signIn.subtitle')}</p>

        <button
          type="button"
          className="devhub-signin__button"
          onClick={handleLogin}
          disabled={busy || checking}
        >
          {logo}
          {buttonLabel}
        </button>

        <p className="devhub-signin__hint">{t(hintKey)}</p>
      </div>

      <p className="devhub-signin__legal">
        &copy; {new Date().getFullYear()} Drägerwerk AG &amp; Co. KGaA —
        Technology for Life
      </p>
    </div>
  );
};

export const DraegerSignInPage = (
  props: SignInPageProps & {
    /** Called (instead of showing the sign-in panel) when the silent restore finds no session. */
    onNoSession?: () => void;
  },
) => {
  const oidcApi = useApi(oidcAuthApiRef);

  // Okta is the only deployed provider (cutover 2026-06-30; the Entra rollback path was
  // removed 2026-10-28). Redirect http://<host>/api/auth/oidc/handler/frame.
  // Localhost keeps the silent guest sign-in (frictionless dev; Playwright e2e runs in guest mode) —
  // UNLESS the real-Okta hook is set (?okta=1 or localStorage 'devhub-e2e-okta'='1'), which lets the
  // e2e suite exercise the genuine Okta oidc flow locally (redirect http://localhost:7007/...). The hook
  // is localhost-only, so deployed behaviour is untouched.
  if (usesGuestAutoSignIn()) {
    const { onNoSession: _unused, ...proxiedProps } = props;
    return <ProxiedSignInPage {...proxiedProps} provider="guest" />;
  }

  return (
    <OAuthSignInScreen
      {...props}
      authApi={oidcApi}
      providerLabel="Dräger"
      hintKey="signIn.hintDraeger"
    />
  );
};
